import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { DASHBOARD_BLOCKED_MESSAGE } from "../utils/dashboardAccess";
import logoImage from "../assets/logo-dark.png";
import "./Chatbot.css";

const storageKeys = {
  token: "chatbot_token",
  user: "chatbot_user",
  username: "chatbot_username",
};

const loadSession = () => {
  try {
    const token = localStorage.getItem(storageKeys.token);
    const userRaw = localStorage.getItem(storageKeys.user);
    const user = userRaw ? JSON.parse(userRaw) : null;
    if (!token || !user) {
      return null;
    }
    return { token, user };
  } catch {
    return null;
  }
};

const formatTime = (value) =>
  new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

function Chatbot() {
  const [session, setSession] = useState(() => loadSession());
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [chatEntries, setChatEntries] = useState([]);
  const [sendError, setSendError] = useState("");
  const [sendLoading, setSendLoading] = useState(false);

  useEffect(() => {
    document.body.classList.add("app-body-chatbot");
    return () => {
      document.body.classList.remove("app-body-chatbot");
    };
  }, []);

  const clientName = useMemo(
    () => session?.user?.client_name || session?.user?.username || "Enviz client",
    [session],
  );

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((current) => ({ ...current, [name]: value }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    setLoginLoading(true);

    try {
      const response = await api.post("/signin", {
        username: loginForm.username.trim(),
        password: loginForm.password,
      });
      const user = {
        ...response.data.client,
        token: response.data.token,
      };

      if (user.effective_dashboard_access === false) {
        setLoginError(DASHBOARD_BLOCKED_MESSAGE);
        return;
      }

      localStorage.setItem(storageKeys.token, response.data.token);
      localStorage.setItem(storageKeys.user, JSON.stringify(user));
      localStorage.setItem(storageKeys.username, response.data.client.username || loginForm.username.trim());

      setSession({ token: response.data.token, user });
      setChatEntries([]);
      setMessage("");
    } catch (error) {
      if (error?.response?.status === 403) {
        setLoginError(error?.response?.data?.detail || DASHBOARD_BLOCKED_MESSAGE);
      } else {
        setLoginError("Invalid username or password.");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(storageKeys.token);
    localStorage.removeItem(storageKeys.user);
    localStorage.removeItem(storageKeys.username);
    setSession(null);
    setChatEntries([]);
    setMessage("");
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const text = message.trim();

    if (!text || !session?.token) {
      return;
    }

    setSendError("");
    setSendLoading(true);

    const pendingEntry = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      text,
      status: "saving",
      timestamp: new Date().toISOString(),
    };

    setChatEntries((current) => [pendingEntry, ...current]);

    try {
      const response = await api.post(
        "/chatbot/message",
        { query: text },
        {
          headers: {
            Authorization: `Bearer ${session.token}`,
          },
        },
      );

      setChatEntries((current) =>
        current.map((entry) =>
          entry.id === pendingEntry.id
            ? {
                ...entry,
                status: "saved",
                chatId: response.data.chat_id,
                savedAt: response.data.timestamp,
              }
            : entry,
        ),
      );
      setMessage("");
    } catch (error) {
      setSendError("Could not save the message. Please try again.");
      setChatEntries((current) => current.filter((entry) => entry.id !== pendingEntry.id));
    } finally {
      setSendLoading(false);
    }
  };

  if (!session) {
    return (
      <div className="chatbot-page">
        <header className="chatbot-topbar">
          <div className="chatbot-brand">
            <img src={logoImage} alt="enViz" className="chatbot-logo" />
            <div>
              <div className="chatbot-title">enViz chatbot</div>
              <div className="chatbot-subtitle">Sign in to save prompts to Postgres</div>
            </div>
          </div>
          <span className="chatbot-badge">/chatbot</span>
        </header>

        <main className="chatbot-login-layout">
          <section className="chatbot-hero">
            <p className="chatbot-eyebrow">Client access</p>
            <h1>Use the same dashboard credentials.</h1>
            <p>
              This page only stores the submitted query. The response engine is not active yet.
            </p>
          </section>

          <section className="chatbot-card chatbot-login-card">
            <div className="chatbot-card-header">
              <img src={logoImage} alt="enViz" className="chatbot-logo small" />
              <div>
                <h2>Login</h2>
                <p>Sign in and start saving messages.</p>
              </div>
            </div>

            <form className="chatbot-form" onSubmit={handleLogin}>
              <label>
                Username
                <input
                  name="username"
                  value={loginForm.username}
                  onChange={handleLoginChange}
                  placeholder="Username"
                  autoComplete="username"
                />
              </label>

              <label>
                Password
                <input
                  name="password"
                  type="password"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </label>

              {loginError && <div className="chatbot-error">{loginError}</div>}

              <button type="submit" className="chatbot-primary" disabled={loginLoading}>
                {loginLoading ? "Signing in..." : "Sign in"}
              </button>
            </form>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="chatbot-page chatbot-page-signed-in">
      <header className="chatbot-topbar">
        <div className="chatbot-brand">
          <img src={logoImage} alt="enViz" className="chatbot-logo" />
          <div>
            <div className="chatbot-title">enViz chatbot</div>
            <div className="chatbot-subtitle">Saved queries only</div>
          </div>
        </div>
        <div className="chatbot-session">
          <span className="chatbot-badge">Logged in</span>
          <div className="chatbot-session-copy">
            <strong>{clientName}</strong>
            <span>{session.user.client_id || "client"}</span>
          </div>
          <button type="button" className="chatbot-ghost" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <main className="chatbot-main">
        <section className="chatbot-card chatbot-panel">
          <div className="chatbot-panel-header">
            <div>
              <p className="chatbot-eyebrow">Enviz chatbot</p>
              <h1>Simple message capture</h1>
            </div>
            <span className="chatbot-badge soft">Stored in Postgres</span>
          </div>

          <div className="chatbot-stream" aria-live="polite">
            {chatEntries.length === 0 ? (
              <div className="chatbot-empty">
                <h3>Start by typing a message below.</h3>
                <p>The chatbot response is not active yet. For now, only your text prompt is saved.</p>
              </div>
            ) : (
              chatEntries.map((entry) => (
                <article key={entry.id} className="chatbot-message-row">
                  <div className="chatbot-message user">
                    <p>{entry.text}</p>
                    <div className="chatbot-message-footer">
                      <small>{formatTime(entry.timestamp)}</small>
                      <small className={entry.status === "saved" ? "saved-text" : ""}>
                        {entry.status === "saved" ? `Saved as ${entry.chatId}` : "Saving..."}
                      </small>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>

          {sendError && <div className="chatbot-error chatbot-send-error">{sendError}</div>}

          <form className="chatbot-composer" onSubmit={handleSend}>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Send a text message..."
              rows={4}
            />
            <div className="chatbot-composer-actions">
              <span>Text only</span>
              <button type="submit" className="chatbot-primary" disabled={sendLoading || !message.trim()}>
                {sendLoading ? "Saving..." : "Send"}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

export default Chatbot;